import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import Header from '@components/Header/Header'
import PlanOption from '@components/PlanOption/PlanOption'
import PhoneNumberOption from '@components/PhoneNumberOption/PhoneNumberOption'
import SearchBar from '@components/SearchBar/SearchBar'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import {
  Container,
  ContentContainer,
  PlanList,
  PlanWrapper,
  SectionLabel,
  PhoneNumberList,
  PhoneNumberWrapper,
  SearchBarWrapper,
  ButtonWrapper
} from './BuyESIM.styles'

type Plan = {
  id: string
  providerIconUrl: string
  title: string
  description: string
  idr: number
}

type PhoneNumber = {
  id: string
  number: string
  price: number | 'free'
}

const BuyESIM = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [step, setStep] = useState<'plan' | 'number'>('plan')
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [selectedNumber, setSelectedNumber] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const plans: Plan[] = [
    {
      id: '1',
      providerIconUrl: 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/phone_providers/indosat.svg',
      title: 'Indosat Unlimited Data Plan',
      description: 'Unlimited data, 30 Days validity',
      idr: 120000
    },
    {
      id: '2',
      providerIconUrl: 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/phone_providers/indosat.svg',
      title: 'Indosat Jagoan Mantap 10GB',
      description: '10GB data, 30 Days validity',
      idr: 55000
    },
    {
      id: '3',
      providerIconUrl: 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/phone_providers/indosat.svg',
      title: 'Indosat Data Combo 15GB',
      description: '15GB data, 30 Days validity',
      idr: 65000
    },
    {
      id: '4',
      providerIconUrl: 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/phone_providers/indosat.svg',
      title: 'Indosat 4G Max 5GB',
      description: '5GB data, 30 Days validity',
      idr: 35000
    },
  ]

  const phoneNumbers: PhoneNumber[] = [
    { id: '1', number: '0812 3456 789', price: 'free' },
    { id: '2', number: '0812 3456 789', price: 'free' },
    { id: '3', number: '0812 3456 790', price: 'free' },
    { id: '4', number: '0812 3456 791', price: 8500 },
    { id: '5', number: '0812 3456 792', price: 15500 },
    { id: '6', number: '0812 3456 793', price: 34500 },
    { id: '7', number: '0812 3456 794', price: 69500 },
  ]

  const filteredNumbers = phoneNumbers.filter(item =>
    item.number.replace(/\s/g, '').includes(searchQuery.replace(/\s/g, ''))
  )

  const handlePlanClick = (plan: Plan) => {
    setSelectedPlan(plan)
    setStep('number')
  }

  const handleNumberClick = (numberId: string) => {
    setSelectedNumber(numberId)
  }

  const handleContinue = () => {
    navigate('/buy-esim-details')
  }

  const handleBack = () => {
    if (step === 'number') {
      setStep('plan')
      setSelectedNumber(null)
    } else {
      navigate(-1)
    }
  }

  if (step === 'plan') {
    return (
      <Container>
        <Header
          title={t('buyESIM.header')}
          showBackButton={true}
          onBackClick={handleBack}
        />
        <ContentContainer>
          <SectionLabel>{t('buyESIM.selectPlan')}</SectionLabel>
          <PlanList>
            {plans.map((plan) => (
              <PlanWrapper key={plan.id} onClick={() => handlePlanClick(plan)}>
                <PlanOption
                  providerIconUrl={plan.providerIconUrl}
                  title={plan.title}
                  description={plan.description}
                  idr={plan.idr}
                />
              </PlanWrapper>
            ))}
          </PlanList>
        </ContentContainer>
      </Container>
    )
  }

  return (
    <Container>
      <Header
        title={t('buyESIM.header')}
        showBackButton={true}
        onBackClick={handleBack}
      />
      <ContentContainer>
        <SearchBarWrapper>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder={t('buyESIM.searchNumber')}
          />
        </SearchBarWrapper>
        <SectionLabel>{t('buyESIM.selectNumber')}</SectionLabel>
        <PhoneNumberList>
          {filteredNumbers.map((phone) => (
            <PhoneNumberWrapper
              key={phone.id}
              onClick={() => handleNumberClick(phone.id)}
            >
              <PhoneNumberOption
                number={phone.number}
                price={phone.price}
                selected={selectedNumber === phone.id}
              />
            </PhoneNumberWrapper>
          ))}
        </PhoneNumberList>
      </ContentContainer>
      <ButtonWrapper>
        <PrimaryButton
          onClick={handleContinue}
          disabled={!selectedNumber}
        >
          {t('buyESIM.continue')}
        </PrimaryButton>
      </ButtonWrapper>
    </Container>
  )
}

export default BuyESIM
